import { useState, useEffect, useCallback } from "react";
import {
  X,
  Phone,
  Mail,
  Calendar,
  Users,
  Star,
  Ban,
  ShieldCheck,
  Edit2,
  CheckCircle,
  XCircle,
  Clock,
  TrendingUp,
  AlertTriangle,
  Loader2,
  Wallet,
  Receipt,
  Coins,
} from "lucide-react";
import { format, parseISO } from "date-fns";
import { es } from "date-fns/locale";
import api from "../lib/api-client";
import { getSourceConfig } from "../lib/reservationSources";
import { formatCents } from "../lib/money";
import { useGlobalToast } from "../contexts/ToastContext";
import { useConfirm } from "../contexts/ConfirmContext";
import { useBookingMode } from "../contexts/LocationContext";
import {
  getEmailStatusConfig,
  optOutReasonLabel,
  type CustomerEmailStatus,
} from "../lib/customerEmailStatus";

// ---------------------------------------------------------------------------
// Tipos (espejo de CustomerStats del backend)
// ---------------------------------------------------------------------------
type AutoTag =
  | "nuevo"
  | "fiel"
  | "en_riesgo"
  | "inactivo"
  | "vip"
  | "bloqueado"
  | "candidato_bloqueo";

/** Lo que ha gastado, según las cuentas del TPV. Null = todavía ninguna. */
interface CustomerSpend {
  visits: number;
  totalCents: number;
  avgBillCents: number;
  avgPerPersonCents: number | null;
  // Null si quien mira no es responsable: el servidor no se la manda.
  avgTipCents: number | null;
  lastBillAt: string;
  currency: string;
}

interface CustomerStats {
  total: number;
  completed: number;
  noShow: number;
  cancelledByCustomer: number;
  cancelledByRestaurant: number;
  upcoming: number;
  byStatus: Record<string, number>;
  bySource: Record<string, number>;
  reliabilityScore: number | null;
  avgPartySize: number | null;
  favoriteShift: string | null;
  favoriteWeekday: number | null;
  firstReservationAt: string | null;
  lastVisitAt: string | null;
  nextReservationAt: string | null;
  autoTags: AutoTag[];
  spend?: CustomerSpend | null;
}

interface ReservationRow {
  id: string;
  date: string;
  time: string;
  partySize: number;
  status: string;
  source: string;
  location?: { name: string } | null;
  table?: { name: string } | null;
  // Modo citas: el backend devuelve especialista/servicio en vez de mesa.
  specialist?: { name: string } | null;
  service?: { name: string } | null;
  // Nombre con el que se hizo la reserva (widget/app). `bookingNameDiffers`
  // lo calcula el backend: solo se pinta cuando no cuadra con la ficha.
  bookingName?: string | null;
  bookingNameDiffers?: boolean;
  // Lo que costó según el TPV. Null = sin cuenta.
  billTotalCents?: number | null;
  billCurrency?: string | null;
}

interface CustomerDetail {
  id: string;
  firstName: string;
  lastName: string;
  email: string | null;
  phone: string;
  countryCode: string;
  language: string;
  notes: string | null;
  tags: string[];
  preferences: unknown;
  vipStatus: boolean;
  marketingConsent: boolean;
  acceptsWhatsApp: boolean;
  blocked: boolean;
  blockedAt: string | null;
  blockedReason: string | null;
  spendOptOut?: boolean;
  createdAt: string;
  reservations: ReservationRow[];
  stats: CustomerStats;
  // Estado de comunicaciones comerciales por email (jul 2026). El endpoint de
  // detalle SÍ devuelve estos 3 campos crudos (`include`, no `select`), pero NO
  // calcula `emailStatus` (requiere el set de rebotes de la organización, que
  // solo calcula el listado) — de ahí el prop `initialEmailStatus` de abajo.
  marketingEmailOptOut: boolean;
  marketingOptOutAt: string | null;
  optOutReason: string | null;
  // La baja puede ser SOLO transaccional (pedida por el cliente): en ese caso
  // el badge dice "De baja" (mismo criterio que la elegibilidad de campañas)
  // pero NO se puede gestionar desde el panel — no es una baja de marketing.
  transactionalEmailOptOut: boolean;
  optOutAt: string | null;
  // Nombres con los que ha reservado y que NO cuadran con la ficha
  // ("a nombre de …"). Vacío = todo en orden.
  bookingNameMismatches?: string[];
}

interface Props {
  customerId: string;
  onClose: () => void;
  onEdit: (customerId: string) => void;
  onChanged: () => void; // refrescar la lista tras bloquear/desbloquear
  /**
   * Estado de comunicaciones ("alta"/"baja"/"rebotado"/"sin_email") tal y como
   * lo calculó el listado (`GET /customers`) para esta fila, en el momento de
   * abrir la ficha. Sirve solo para pintar el badge inicial correctamente
   * (incluida la distinción "rebotado", que el endpoint de detalle no calcula).
   * Tras cualquier baja/alta manual, el estado se actualiza con el valor fresco
   * que devuelve la propia respuesta del PATCH.
   */
  initialEmailStatus?: CustomerEmailStatus;
}

/** Badge discreto de estado de comunicaciones por email (mismo criterio que Customers.tsx). */
function EmailStatusBadge({ status }: { status?: CustomerEmailStatus }) {
  const cfg = getEmailStatusConfig(status);
  // Estado desconocido (p. ej. backend antiguo durante un despliegue): sin
  // badge, mejor que etiquetar "Alta" a quien puede estar de baja.
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.badgeClassName}`}
    >
      <Icon className="w-3 h-3" />
      {cfg.label}
    </span>
  );
}

/** Mensaje de error de la API (en vez del genérico de axios). */
function getActionErrorMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const response = (err as { response?: { data?: { message?: string } } })
      .response;
    return response?.data?.message || fallback;
  }
  return fallback;
}

// ---------------------------------------------------------------------------
// Config visual
// ---------------------------------------------------------------------------
const TAG_CONFIG: Record<AutoTag, { label: string; className: string }> = {
  vip: { label: "VIP", className: "bg-purple-100 text-purple-700" },
  fiel: { label: "Cliente fiel", className: "bg-emerald-100 text-emerald-700" },
  nuevo: { label: "Nuevo", className: "bg-blue-100 text-blue-700" },
  en_riesgo: { label: "En riesgo", className: "bg-orange-100 text-orange-700" },
  inactivo: { label: "Inactivo", className: "bg-gray-200 text-gray-700" },
  bloqueado: { label: "Bloqueado", className: "bg-red-100 text-red-700" },
  candidato_bloqueo: {
    label: "Candidato a bloqueo",
    className: "bg-rose-100 text-rose-700",
  },
};

const STATUS_LABELS: Record<string, { label: string; className: string }> = {
  pending: { label: "Pendiente", className: "bg-yellow-100 text-yellow-700" },
  confirmed: { label: "Confirmada", className: "bg-blue-100 text-blue-700" },
  double_confirmed: {
    label: "Reconfirmada",
    className: "bg-green-100 text-green-700",
  },
  arrived: { label: "Llegó", className: "bg-purple-100 text-purple-700" },
  seated: { label: "Sentado", className: "bg-indigo-100 text-indigo-700" },
  completed: { label: "Completada", className: "bg-emerald-100 text-emerald-700" },
  cancelled: { label: "Cancelada", className: "bg-red-100 text-red-700" },
  cancelled_by_restaurant: {
    label: "Cancelada (negocio)",
    className: "bg-rose-100 text-rose-700",
  },
  no_show: { label: "No-show", className: "bg-gray-200 text-gray-700" },
};

const WEEKDAYS = [
  "Domingo",
  "Lunes",
  "Martes",
  "Miércoles",
  "Jueves",
  "Viernes",
  "Sábado",
];

const SHIFT_LABELS: Record<string, string> = {
  desayuno: "Desayuno",
  brunch: "Brunch",
  comida: "Comida",
  cena: "Cena",
  // Modo citas: franjas genéricas devueltas por `getCustomerStats`.
  manana: "Mañana",
  tarde: "Tarde",
};

type Tab = "resumen" | "historial" | "canales" | "notas";

// ---------------------------------------------------------------------------
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "d MMM yyyy", { locale: es });
  } catch {
    return "—";
  }
}

function statusBadge(status: string) {
  const cfg = STATUS_LABELS[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
}

export default function CustomerDetailModal({
  customerId,
  onClose,
  onEdit,
  onChanged,
  initialEmailStatus,
}: Props) {
  const toast = useGlobalToast();
  const { confirm } = useConfirm();
  const isAppointmentsMode = useBookingMode() === "appointments";
  const reservationsNoun = isAppointmentsMode ? "citas" : "reservas";

  const [customer, setCustomer] = useState<CustomerDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState<Tab>("resumen");
  const [busy, setBusy] = useState(false);
  // Comunicaciones por email: estado del badge + estado de carga de las acciones.
  const [emailStatus, setEmailStatus] = useState<CustomerEmailStatus | undefined>(
    initialEmailStatus,
  );
  const [commsBusy, setCommsBusy] = useState(false);

  // Bloqueo: mini-modal con motivo
  const [showBlockForm, setShowBlockForm] = useState(false);
  const [blockReason, setBlockReason] = useState("");

  // Historial paginado
  const [history, setHistory] = useState<ReservationRow[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  const fetchCustomer = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get(`/customers/${customerId}`);
      setCustomer(res.data.data);
    } catch {
      toast.showToast("No se pudo cargar la ficha del cliente", "error");
    } finally {
      setLoading(false);
    }
  }, [customerId, toast]);

  useEffect(() => {
    fetchCustomer();
  }, [fetchCustomer]);

  const fetchHistory = useCallback(
    async (page: number) => {
      setHistoryLoading(true);
      try {
        const res = await api.get(`/customers/${customerId}/reservations`, {
          params: { page, limit: 20 },
        });
        setHistory(res.data.data);
        setHistoryPage(res.data.pagination.page);
        setHistoryTotalPages(res.data.pagination.totalPages);
      } catch {
        toast.showToast("No se pudo cargar el historial", "error");
      } finally {
        setHistoryLoading(false);
      }
    },
    [customerId, toast],
  );

  useEffect(() => {
    if (tab === "historial" && history.length === 0) {
      fetchHistory(1);
    }
  }, [tab, history.length, fetchHistory]);

  const handleBlock = async () => {
    setBusy(true);
    try {
      await api.patch(`/customers/${customerId}/block`, {
        reason: blockReason.trim() || undefined,
      });
      toast.showToast("Cliente bloqueado", "success");
      setShowBlockForm(false);
      setBlockReason("");
      await fetchCustomer();
      onChanged();
    } catch {
      toast.showToast("No se pudo bloquear el cliente", "error");
    } finally {
      setBusy(false);
    }
  };

  const handleUnblock = async () => {
    const ok = await confirm({
      title: "Desbloquear cliente",
      message:
        "El cliente podrá volver a reservar online por todos los canales. ¿Continuar?",
      confirmText: "Desbloquear",
    });
    if (!ok) return;
    setBusy(true);
    try {
      await api.patch(`/customers/${customerId}/unblock`);
      toast.showToast("Cliente desbloqueado", "success");
      await fetchCustomer();
      onChanged();
    } catch {
      toast.showToast("No se pudo desbloquear el cliente", "error");
    } finally {
      setBusy(false);
    }
  };

  // Baja/alta MANUAL de comunicaciones comerciales por email. Solo afecta a la
  // fila Customer de esta organización (sin propagación cross-org).
  const handleMarketingOptOut = async () => {
    const ok = await confirm({
      title: "Dar de baja de comunicaciones comerciales",
      message:
        "El cliente dejará de recibir campañas de email (novedades, promociones...). Los emails transaccionales (confirmaciones de reserva, recordatorios) no se ven afectados. ¿Continuar?",
      confirmText: "Dar de baja",
    });
    if (!ok) return;
    setCommsBusy(true);
    try {
      const res = await api.patch(`/customers/${customerId}/marketing-opt-out`);
      setCustomer((prev) => (prev ? { ...prev, ...res.data.data } : prev));
      setEmailStatus(res.data.data.emailStatus);
      toast.showToast(
        res.data.message || "Cliente dado de baja de comunicaciones comerciales",
        "success",
      );
      onChanged();
    } catch (err) {
      toast.showToast(
        getActionErrorMessage(err, "No se pudo dar de baja al cliente"),
        "error",
      );
    } finally {
      setCommsBusy(false);
    }
  };

  const handleMarketingOptIn = async () => {
    const ok = await confirm({
      title: "Dar de alta en comunicaciones comerciales",
      message:
        "El cliente volverá a recibir campañas de email de tu negocio. ¿Continuar?",
      confirmText: "Dar de alta",
    });
    if (!ok) return;
    setCommsBusy(true);
    try {
      const res = await api.patch(`/customers/${customerId}/marketing-opt-in`);
      setCustomer((prev) => (prev ? { ...prev, ...res.data.data } : prev));
      setEmailStatus(res.data.data.emailStatus);
      toast.showToast(
        res.data.message || "Cliente dado de alta en comunicaciones comerciales",
        "success",
      );
      onChanged();
    } catch (err) {
      // Si la baja la pidió el propio cliente, el backend responde 409 con un
      // mensaje explicativo (RGPD Art. 21) — se muestra tal cual, sin fallback.
      toast.showToast(
        getActionErrorMessage(err, "No se pudo dar de alta al cliente"),
        "error",
      );
    } finally {
      setCommsBusy(false);
    }
  };

  const stats = customer?.stats;

  const handleSpendOptOut = async () => {
    if (!customer) return;
    const optOut = !customer.spendOptOut;
    if (optOut) {
      const ok = await confirm({
        title: "¿No guardar el gasto de este cliente?",
        message:
          "Se borra lo guardado y no se guardará lo de sus próximas reservas. No afecta a sus reservas ni a cómo se le atiende.",
        confirmText: "No guardar su gasto",
        danger: true,
      });
      if (!ok) return;
    }
    try {
      await api.patch(`/customers/${customer.id}/spend-opt-out`, { optOut });
      toast.showToast(optOut ? "Gasto borrado" : "Se volverá a guardar su gasto", "success");
      await fetchCustomer();
    } catch (err) {
      toast.showToast(getActionErrorMessage(err, "No se ha podido guardar el cambio"), "error");
    }
  };
  const fullName = customer
    ? `${customer.firstName} ${customer.lastName}`.trim()
    : "";

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90dvh] flex flex-col">
        {/* Cabecera */}
        <div className="flex items-start justify-between p-5 border-b">
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h2 className="text-xl font-bold text-gray-900 truncate">
                {loading ? "Cargando…" : fullName || "Cliente"}
              </h2>
              {customer?.vipStatus && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-purple-100 text-purple-700">
                  <Star className="w-3 h-3" /> VIP
                </span>
              )}
              {customer?.blocked && (
                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">
                  <Ban className="w-3 h-3" /> Bloqueado
                </span>
              )}
            </div>
            {customer && (
              <p className="text-sm text-gray-500 mt-1">
                Cliente desde {fmtDate(customer.createdAt)}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 p-1"
            aria-label="Cerrar"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {loading || !customer || !stats ? (
          <div className="flex-1 flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
          </div>
        ) : (
          <>
            {/* Pestañas */}
            <div className="flex border-b px-5 gap-1">
              {(
                [
                  ["resumen", "Resumen"],
                  ["historial", "Historial"],
                  ["canales", "Canales"],
                  ["notas", "Notas"],
                ] as [Tab, string][]
              ).map(([key, label]) => (
                <button
                  key={key}
                  onClick={() => setTab(key)}
                  className={`px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${
                    tab === key
                      ? "border-blue-600 text-blue-600"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            <div className="flex-1 overflow-y-auto p-5">
              {/* ----------------------- RESUMEN ----------------------- */}
              {tab === "resumen" && (
                <div className="space-y-5">
                  {/* Contacto */}
                  <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                    <span className="inline-flex items-center gap-1.5 text-gray-700">
                      <Phone className="w-4 h-4 text-gray-400" />
                      {customer.countryCode} {customer.phone || "—"}
                    </span>
                    <span className="inline-flex items-center gap-1.5 text-gray-700">
                      <Mail className="w-4 h-4 text-gray-400" />
                      {customer.email || "—"}
                    </span>
                  </div>

                  {/* Etiquetas automáticas */}
                  {stats.autoTags.length > 0 && (
                    <div className="flex flex-wrap gap-2">
                      {stats.autoTags.map((t) => (
                        <span
                          key={t}
                          className={`px-2 py-0.5 rounded-full text-xs font-medium ${TAG_CONFIG[t].className}`}
                        >
                          {TAG_CONFIG[t].label}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Score de fiabilidad */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-1.5">
                        <TrendingUp className="w-4 h-4 text-gray-400" />
                        Fiabilidad
                      </span>
                      <span className="text-sm font-bold text-gray-900">
                        {stats.reliabilityScore === null
                          ? "—"
                          : `${stats.reliabilityScore}%`}
                      </span>
                    </div>
                    <div className="h-2 bg-gray-200 rounded-full overflow-hidden">
                      <div
                        className={`h-full rounded-full ${
                          (stats.reliabilityScore ?? 0) >= 70
                            ? "bg-emerald-500"
                            : (stats.reliabilityScore ?? 0) >= 40
                              ? "bg-amber-500"
                              : "bg-red-500"
                        }`}
                        style={{ width: `${stats.reliabilityScore ?? 0}%` }}
                      />
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Asistencias sobre {reservationsNoun} evaluables
                      (completadas + no-shows + cancelaciones del cliente).
                    </p>
                  </div>

                  {/* KPIs */}
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    <KpiCard
                      icon={<Calendar className="w-4 h-4" />}
                      label={isAppointmentsMode ? "Citas totales" : "Reservas totales"}
                      value={stats.total}
                    />
                    <KpiCard
                      icon={<CheckCircle className="w-4 h-4 text-emerald-500" />}
                      label="Completadas"
                      value={stats.completed}
                    />
                    <KpiCard
                      icon={<XCircle className="w-4 h-4 text-gray-500" />}
                      label="No-shows"
                      value={stats.noShow}
                      highlight={stats.noShow > 0}
                    />
                    <KpiCard
                      icon={<X className="w-4 h-4 text-red-500" />}
                      label="Canceló (cliente)"
                      value={stats.cancelledByCustomer}
                    />
                    {/* Comensales medios no aplica en modo citas (partySize
                        siempre 1): el backend manda avgPartySize = null. */}
                    {!isAppointmentsMode && (
                      <KpiCard
                        icon={<Users className="w-4 h-4 text-gray-500" />}
                        label="Comensales medios"
                        value={stats.avgPartySize ?? "—"}
                      />
                    )}
                    <KpiCard
                      icon={<Clock className="w-4 h-4 text-gray-500" />}
                      label="Próximas"
                      value={stats.upcoming}
                    />
                  </div>

                  {/* Gasto: solo si el TPV ha enviado alguna cuenta */}
                  {stats.spend && (
                    <div>
                      <h3 className="text-sm font-semibold text-gray-900 mb-2">
                        Gasto (últimos 24 meses)
                      </h3>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        <KpiCard
                          icon={<Wallet className="w-4 h-4 text-gray-500" />}
                          label="Gasto"
                          value={formatCents(stats.spend.totalCents, stats.spend.currency)}
                        />
                        <KpiCard
                          icon={<Receipt className="w-4 h-4 text-gray-500" />}
                          label="Cuenta media"
                          value={formatCents(stats.spend.avgBillCents, stats.spend.currency)}
                        />
                        <KpiCard
                          icon={<Users className="w-4 h-4 text-gray-500" />}
                          label="Por persona"
                          value={
                            stats.spend.avgPerPersonCents === null
                              ? "—"
                              : formatCents(stats.spend.avgPerPersonCents, stats.spend.currency)
                          }
                        />
                        {stats.spend.avgTipCents !== null && (
                          <KpiCard
                            icon={<Coins className="w-4 h-4 text-gray-500" />}
                            label="Propina media"
                            value={formatCents(stats.spend.avgTipCents, stats.spend.currency)}
                          />
                        )}
                        <KpiCard
                          icon={<CheckCircle className="w-4 h-4 text-emerald-500" />}
                          label="Visitas con cuenta"
                          value={stats.spend.visits}
                        />
                        <KpiCard
                          icon={<Calendar className="w-4 h-4 text-gray-500" />}
                          label="Última cuenta"
                          value={fmtDate(stats.spend.lastBillAt)}
                        />
                      </div>
                    </div>
                  )}

                  {/* Oposición del cliente a guardar su gasto (RGPD Art. 21) */}
                  {!isAppointmentsMode && (stats.spend || customer.spendOptOut) && (
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
                      <span className="text-gray-500">
                        {customer.spendOptOut
                          ? "Este cliente no quiere que se guarde su gasto."
                          : "Si el cliente se opone, se borra lo guardado."}
                      </span>
                      <button
                        type="button"
                        onClick={handleSpendOptOut}
                        className="text-sm font-medium text-gray-700 underline hover:text-gray-900"
                      >
                        {customer.spendOptOut
                          ? "Volver a guardar su gasto"
                          : "No guardar el gasto de este cliente"}
                      </button>
                    </div>
                  )}

                  {/* Preferencias temporales / visitas */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <InfoRow
                      label="Última visita"
                      value={fmtDate(stats.lastVisitAt)}
                    />
                    <InfoRow
                      label="Próxima reserva"
                      value={fmtDate(stats.nextReservationAt)}
                    />
                    <InfoRow
                      label="Día favorito"
                      value={
                        stats.favoriteWeekday !== null
                          ? WEEKDAYS[stats.favoriteWeekday]
                          : "—"
                      }
                    />
                    <InfoRow
                      label="Franja favorita"
                      value={
                        stats.favoriteShift
                          ? SHIFT_LABELS[stats.favoriteShift] ??
                            stats.favoriteShift
                          : "—"
                      }
                    />
                  </div>

                  {/* Comunicaciones comerciales por email */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                      <span className="text-sm font-medium text-gray-700 inline-flex items-center gap-1.5">
                        <Mail className="w-4 h-4 text-gray-400" />
                        Comunicaciones comerciales
                      </span>
                      <EmailStatusBadge status={emailStatus} />
                    </div>

                    {customer.marketingEmailOptOut && (
                      <div className="text-xs text-gray-500 space-y-0.5 mb-3">
                        <p>
                          Baja el{" "}
                          {customer.marketingOptOutAt
                            ? fmtDate(customer.marketingOptOutAt)
                            : "—"}
                        </p>
                        <p>
                          Motivo: {optOutReasonLabel(customer.optOutReason)}
                        </p>
                      </div>
                    )}
                    {/* Baja SOLO transaccional: el badge dice "De baja" pero no
                        es una baja de marketing — la pidió el cliente para sus
                        emails transaccionales y el panel no puede gestionarla. */}
                    {!customer.marketingEmailOptOut &&
                      customer.transactionalEmailOptOut && (
                        <div className="text-xs text-gray-500 space-y-0.5 mb-3">
                          <p>
                            Baja de emails transaccionales solicitada por el
                            cliente
                            {customer.optOutAt
                              ? ` el ${fmtDate(customer.optOutAt)}`
                              : ""}
                            .
                          </p>
                          <p>
                            Motivo: {optOutReasonLabel(customer.optOutReason)}
                          </p>
                          <p>
                            Solo el cliente puede revertirla desde el enlace de
                            preferencias de sus emails.
                          </p>
                        </div>
                      )}
                    {!customer.marketingEmailOptOut &&
                      emailStatus === "sin_email" && (
                        <p className="text-xs text-gray-500 mb-3">
                          Este cliente no tiene un email utilizable: no puede
                          recibir campañas comerciales.
                        </p>
                      )}
                    {!customer.marketingEmailOptOut &&
                      emailStatus === "rebotado" && (
                        <p className="text-xs text-orange-600 mb-3">
                          El email de este cliente ha rebotado en envíos
                          anteriores. Comprueba que la dirección sea correcta.
                        </p>
                      )}

                    {/* Con una baja SOLO transaccional no se ofrece ninguna
                        acción: "Dar de baja" (de marketing) confundiría — el
                        estado visible no cambiaría — y el alta no es del panel. */}
                    {customer.marketingEmailOptOut ? (
                      <button
                        onClick={handleMarketingOptIn}
                        disabled={commsBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                      >
                        {commsBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        Dar de alta
                      </button>
                    ) : customer.transactionalEmailOptOut ? null : (
                      <button
                        onClick={handleMarketingOptOut}
                        disabled={commsBusy}
                        className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                      >
                        {commsBusy && <Loader2 className="w-4 h-4 animate-spin" />}
                        Dar de baja
                      </button>
                    )}
                  </div>

                  {/* Reservas hechas "a otro nombre" (widget/app): aviso para
                      revisar la ficha — puede ser un grupo/empresa o un error. */}
                  {customer.bookingNameMismatches &&
                    customer.bookingNameMismatches.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm">
                        <p className="font-medium text-amber-800 flex items-center gap-1.5">
                          <AlertTriangle className="w-4 h-4" />
                          Ha reservado con otro nombre
                        </p>
                        <p className="text-amber-800 mt-1">
                          {customer.bookingNameMismatches.map((n, i) => (
                            <span key={n}>
                              {i > 0 && ", "}
                              <strong>«{n}»</strong>
                            </span>
                          ))}
                        </p>
                        <p className="text-amber-700 mt-1 text-xs">
                          Puede ser una reserva a nombre de un grupo o empresa,
                          o un error en la ficha. Si el nombre de la ficha no
                          es el suyo, corrígelo con «Editar datos».
                        </p>
                      </div>
                    )}

                  {/* Motivo de bloqueo */}
                  {customer.blocked && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm">
                      <p className="font-medium text-red-800">
                        Cliente bloqueado el {fmtDate(customer.blockedAt)}
                      </p>
                      {customer.blockedReason && (
                        <p className="text-red-700 mt-0.5">
                          Motivo: {customer.blockedReason}
                        </p>
                      )}
                      <p className="text-red-600 mt-1 text-xs">
                        No puede reservar online. El panel sí permite crearle
                        reservas manualmente.
                      </p>
                    </div>
                  )}
                </div>
              )}

              {/* ----------------------- HISTORIAL ----------------------- */}
              {tab === "historial" && (
                <div>
                  {historyLoading && history.length === 0 ? (
                    <div className="flex justify-center py-10">
                      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
                    </div>
                  ) : history.length === 0 ? (
                    <p className="text-center text-gray-400 py-10 text-sm">
                      Este cliente no tiene {reservationsNoun} registradas.
                    </p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        {history.map((r) => {
                          const sc = getSourceConfig(r.source);
                          const Icon = sc.icon;
                          return (
                            <div
                              key={r.id}
                              className="flex items-center justify-between gap-3 border rounded-lg p-3 text-sm"
                            >
                              <div className="min-w-0">
                                <div className="font-medium text-gray-900">
                                  {fmtDate(r.date)} · {r.time}
                                </div>
                                {r.bookingNameDiffers && r.bookingName && (
                                  <div className="text-xs text-amber-700 truncate">
                                    a nombre de {r.bookingName}
                                  </div>
                                )}
                                <div className="text-gray-500 flex items-center gap-2 mt-0.5 flex-wrap">
                                  <span className="inline-flex items-center gap-1">
                                    <Users className="w-3 h-3" /> {r.partySize}
                                  </span>
                                  {r.service?.name || r.specialist?.name ? (
                                    <span>
                                      ·{" "}
                                      {[r.service?.name, r.specialist?.name]
                                        .filter(Boolean)
                                        .join(" · ")}
                                    </span>
                                  ) : (
                                    r.table?.name && (
                                      <span>· Mesa {r.table.name}</span>
                                    )
                                  )}
                                  <span
                                    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded ${sc.bgColor} ${sc.color}`}
                                  >
                                    <Icon className="w-3 h-3" /> {sc.label}
                                  </span>
                                </div>
                              </div>
                              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                                {r.billTotalCents != null && (
                                  <span className="font-semibold text-gray-900">
                                    {formatCents(r.billTotalCents, r.billCurrency ?? "EUR")}
                                  </span>
                                )}
                                {statusBadge(r.status)}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                      {historyTotalPages > 1 && (
                        <div className="flex items-center justify-center gap-3 mt-4 text-sm">
                          <button
                            disabled={historyPage <= 1 || historyLoading}
                            onClick={() => fetchHistory(historyPage - 1)}
                            className="px-3 py-1 rounded border disabled:opacity-40"
                          >
                            Anterior
                          </button>
                          <span className="text-gray-500">
                            {historyPage} / {historyTotalPages}
                          </span>
                          <button
                            disabled={
                              historyPage >= historyTotalPages || historyLoading
                            }
                            onClick={() => fetchHistory(historyPage + 1)}
                            className="px-3 py-1 rounded border disabled:opacity-40"
                          >
                            Siguiente
                          </button>
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}

              {/* ----------------------- CANALES ----------------------- */}
              {tab === "canales" && (
                <div>
                  {Object.keys(stats.bySource).length === 0 ? (
                    <p className="text-center text-gray-400 py-10 text-sm">
                      Sin datos de canal todavía.
                    </p>
                  ) : (
                    <div className="space-y-3">
                      {Object.entries(stats.bySource)
                        .sort((a, b) => b[1] - a[1])
                        .map(([src, count]) => {
                          const sc = getSourceConfig(src);
                          const Icon = sc.icon;
                          const pct = stats.total
                            ? Math.round((count / stats.total) * 100)
                            : 0;
                          return (
                            <div key={src}>
                              <div className="flex items-center justify-between text-sm mb-1">
                                <span className="inline-flex items-center gap-1.5 text-gray-700">
                                  <Icon className={`w-4 h-4 ${sc.color}`} />
                                  {sc.label}
                                </span>
                                <span className="text-gray-500">
                                  {count} ({pct}%)
                                </span>
                              </div>
                              <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                                <div
                                  className="h-full bg-blue-500 rounded-full"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                            </div>
                          );
                        })}
                    </div>
                  )}
                </div>
              )}

              {/* ----------------------- NOTAS ----------------------- */}
              {tab === "notas" && (
                <div className="space-y-4 text-sm">
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">
                      Notas internas
                    </h3>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {customer.notes || "Sin notas."}
                    </p>
                  </div>
                  <div>
                    <h3 className="font-medium text-gray-700 mb-1">
                      Preferencias / restricciones
                    </h3>
                    <p className="text-gray-600 whitespace-pre-wrap">
                      {customer.preferences
                        ? JSON.stringify(customer.preferences, null, 2)
                        : "Sin preferencias registradas."}
                    </p>
                  </div>
                  {customer.tags.length > 0 && (
                    <div>
                      <h3 className="font-medium text-gray-700 mb-1">
                        Etiquetas manuales
                      </h3>
                      <div className="flex flex-wrap gap-2">
                        {customer.tags.map((t) => (
                          <span
                            key={t}
                            className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-700"
                          >
                            {t}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Pie de acciones */}
            <div className="flex items-center justify-between gap-2 p-4 border-t">
              <div>
                {customer.blocked ? (
                  <button
                    onClick={handleUnblock}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-emerald-50 text-emerald-700 hover:bg-emerald-100 disabled:opacity-50"
                  >
                    <ShieldCheck className="w-4 h-4" /> Desbloquear
                  </button>
                ) : (
                  <button
                    onClick={() => setShowBlockForm(true)}
                    disabled={busy}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-50 text-red-700 hover:bg-red-100 disabled:opacity-50"
                  >
                    <Ban className="w-4 h-4" /> Bloquear
                  </button>
                )}
              </div>
              <button
                onClick={() => onEdit(customer.id)}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                <Edit2 className="w-4 h-4" /> Editar datos
              </button>
            </div>
          </>
        )}

        {/* Mini-modal de bloqueo con motivo */}
        {showBlockForm && (
          <div className="absolute inset-0 bg-black/40 flex items-center justify-center p-4 rounded-xl">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-5">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                <h3 className="text-lg font-semibold text-gray-900">
                  Bloquear cliente
                </h3>
              </div>
              <p className="text-sm text-gray-600 mb-3">
                No podrá reservar online (web, IA, app, Google). El panel sí
                permite crearle reservas manualmente.
              </p>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Motivo (opcional)
              </label>
              <textarea
                value={blockReason}
                onChange={(e) => setBlockReason(e.target.value)}
                rows={3}
                placeholder="Ej. 3 no-shows consecutivos"
                className="w-full border rounded-lg p-2 text-sm focus:ring-2 focus:ring-red-400 focus:outline-none"
              />
              <div className="flex justify-end gap-2 mt-4">
                <button
                  onClick={() => {
                    setShowBlockForm(false);
                    setBlockReason("");
                  }}
                  disabled={busy}
                  className="px-3 py-2 rounded-lg text-sm font-medium text-gray-600 hover:bg-gray-100"
                >
                  Cancelar
                </button>
                <button
                  onClick={handleBlock}
                  disabled={busy}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-red-600 text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                  Bloquear
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function KpiCard({
  icon,
  label,
  value,
  highlight,
}: {
  icon: React.ReactNode;
  label: string;
  value: number | string;
  highlight?: boolean;
}) {
  return (
    <div
      className={`rounded-lg border p-3 ${
        highlight ? "border-gray-300 bg-gray-50" : "bg-white"
      }`}
    >
      <div className="flex items-center gap-1.5 text-gray-500 text-xs">
        {icon}
        {label}
      </div>
      <div className="text-xl font-bold text-gray-900 mt-1">{value}</div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-gray-50 rounded-lg px-3 py-2">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="text-gray-800 font-medium">{value}</div>
    </div>
  );
}
